<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import router from '../../../../index';
import EvolutionApi from '../components/EvolutionApi.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const copy = {
  created: 'Conexão criada. Escaneie o QR Code para vincular seu WhatsApp.',
  title: 'Crie sua conexão',
  description:
    'Escolha como conectar este canal e centralizar seus atendimentos.',
  type: 'Tipo de conexão',
  name: 'Nome da conexão',
  namePlaceholder: 'Ex.: Atendimento comercial',
  urlPlaceholder: 'https://seusistema.com.br/webhook',
  next: 'O QR Code será exibido na próxima etapa. Tenha seu celular por perto.',
};

const shouldBeWebhookUrl = (value = '') =>
  value ? value.startsWith('http') : true;

const store = useStore();
const { t } = useI18n();

const createdInbox = ref(null);
const channelName = ref('');
const webhookUrl = ref('');
const isEvolutionApiIntegration = ref(false);

watch(isEvolutionApiIntegration, value => {
  if (value) webhookUrl.value = '';
});

const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const rules = {
  channelName: { required },
  webhookUrl: { shouldBeWebhookUrl },
};

const v$ = useVuelidate(rules, { channelName, webhookUrl });

const goToAgents = inboxId =>
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: { page: 'new', inbox_id: inboxId },
  });

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    const apiChannel = await store.dispatch('inboxes/createChannel', {
      name: channelName.value?.trim(),
      channel: {
        type: 'api',
        webhook_url: webhookUrl.value,
        additional_attributes: {
          is_evolution_api_integration: isEvolutionApiIntegration.value,
        },
      },
    });

    if (isEvolutionApiIntegration.value) {
      createdInbox.value = apiChannel;
      return;
    }
    goToAgents(apiChannel.id);
  } catch (error) {
    useAlert(t('INBOX_MGMT.ADD.API_CHANNEL.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="col-span-6 w-full p-5 md:p-8">
    <template v-if="createdInbox">
      <div
        class="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-n-teal-7 bg-n-teal-2 p-4"
      >
        <div>
          <p class="m-0 font-semibold text-n-slate-12">
            {{ createdInbox.name || channelName }}
          </p>
          <p class="m-0 mt-1 text-sm text-n-slate-11">
            {{ copy.created }}
          </p>
        </div>
        <NextButton
          label="Adicionar atendentes"
          icon="i-lucide-arrow-right"
          @click="goToAgents(createdInbox.id)"
        />
      </div>
      <EvolutionApi :inbox="createdInbox" />
    </template>
    <template v-else>
      <header class="mb-6">
        <span
          class="mb-3 grid size-11 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
          ><span class="i-lucide-plug size-6"
        /></span>
        <h2 class="m-0 text-xl font-semibold text-n-slate-12">
          {{ copy.title }}
        </h2>
        <p class="mt-2 text-sm text-n-slate-11">
          {{ copy.description }}
        </p>
      </header>
      <form class="space-y-5" @submit.prevent="createChannel">
        <fieldset class="grid gap-3 sm:grid-cols-2">
          <legend class="mb-2 text-sm font-medium text-n-slate-12">
            {{ copy.type }}
          </legend>
          <label
            v-for="option in [
              {
                value: true,
                title: 'WhatsApp por QR Code',
                description: 'Conecte pelo celular, sem configurar um webhook.',
                icon: 'i-lucide-qr-code',
              },
              {
                value: false,
                title: 'Integração API',
                description: 'Receba mensagens e eventos no seu sistema.',
                icon: 'i-lucide-webhook',
              },
            ]"
            :key="String(option.value)"
            class="m-0 flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors hover:bg-n-alpha-2"
            :class="
              isEvolutionApiIntegration === option.value
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-weak bg-n-solid-1'
            "
          >
            <input
              v-model="isEvolutionApiIntegration"
              type="radio"
              :value="option.value"
              name="connection-type"
              class="mt-1"
            />
            <span class="min-w-0"
              ><span
                class="mb-2 block size-5 text-n-brand"
                :class="option.icon"
              /><span class="block text-sm font-semibold text-n-slate-12">{{
                option.title
              }}</span
              ><span
                class="mt-1 block text-xs leading-relaxed text-n-slate-11"
                >{{ option.description }}</span
              ></span
            >
          </label>
        </fieldset>
        <div class="space-y-4 rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <label :class="{ error: v$.channelName.$error }">
            {{ copy.name }}
            <input
              v-model="channelName"
              type="text"
              :placeholder="copy.namePlaceholder"
              @blur="v$.channelName.$touch"
            />
            <span v-if="v$.channelName.$error" class="message">{{
              $t('INBOX_MGMT.ADD.API_CHANNEL.CHANNEL_NAME.ERROR')
            }}</span>
          </label>
          <label
            v-if="!isEvolutionApiIntegration"
            :class="{ error: v$.webhookUrl.$error }"
          >
            {{ $t('INBOX_MGMT.ADD.API_CHANNEL.WEBHOOK_URL.LABEL') }}
            <input
              v-model="webhookUrl"
              type="url"
              :placeholder="copy.urlPlaceholder"
              @blur="v$.webhookUrl.$touch"
            />
            <span class="mt-1 block text-xs text-n-slate-11">{{
              $t('INBOX_MGMT.ADD.API_CHANNEL.WEBHOOK_URL.SUBTITLE')
            }}</span>
          </label>
          <p v-else class="m-0 flex items-center gap-2 text-sm text-n-slate-11">
            <span class="i-lucide-smartphone size-4 shrink-0" />{{ copy.next }}
          </p>
        </div>
        <NextButton
          :is-loading="uiFlags.isCreating"
          :disabled="uiFlags.isCreating"
          type="submit"
          :icon="
            isEvolutionApiIntegration ? 'i-lucide-qr-code' : 'i-lucide-plus'
          "
          :label="
            isEvolutionApiIntegration
              ? 'Criar e conectar WhatsApp'
              : 'Criar integração API'
          "
        />
      </form>
    </template>
  </div>
</template>
